import api from '../../api/restaurant/restaurantRecipeApi';
import {
  CustomResponse,
  RestaurantRecipeDetailDTO,
  RestaurantRecipeDTO,
  RestaurantRecipeFilters,
  RestaurantRecipeForm,
  RestaurantPageRequest,
  RestaurantRecipePage,
  SpringPage,
} from '../../types/restaurant/RestaurantRecipeTypes';
import { restaurantPageQuery } from './restaurantPageQuery';

const INVENTORY_MODES = [
  'DIRECT_ORDER_CONSUMPTION',
  'PRODUCTION_BATCH',
] as const;

export const isRestaurantRecipeInventoryMode = (
  value: unknown
): value is RestaurantRecipeForm['inventoryMode'] =>
  INVENTORY_MODES.includes(value as (typeof INVENTORY_MODES)[number]);

export const restaurantRecipePayload = (form: RestaurantRecipeForm) => {
  if (!isRestaurantRecipeInventoryMode(form.inventoryMode)) {
    throw new Error('Seleccione un modo de consumo de inventario válido.');
  }

  return {
    menuItemId: Number(form.menuItemId),
    name: form.name.trim(),
    description: form.description.trim() || null,
    yieldQuantity: Number(form.yieldQuantity),
    yieldUnitId: Number(form.yieldUnitId),
    inventoryMode: form.inventoryMode,
    active: form.active,
    ingredients: form.ingredients.map((ingredient, index) => ({
      rawMaterialId: Number(ingredient.rawMaterialId),
      quantity: Number(ingredient.quantity),
      unitId: Number(ingredient.unitId),
      notes: ingredient.notes.trim() || null,
      orderIndex: index,
    })),
  };
};

const RestaurantRecipeService = {
  list: async (
    branchId: number,
    filters: {
      search?: string;
      active?: boolean;
      menuItemId?: number;
      page?: number;
      size?: number;
    } = {}
  ) => {
    const query = new URLSearchParams({
      page: String(filters.page ?? 0),
      size: String(filters.size ?? 20),
    });
    if (filters.search?.trim()) query.set('search', filters.search.trim());
    if (filters.active !== undefined)
      query.set('active', String(filters.active));
    if (filters.menuItemId) query.set('menuItemId', String(filters.menuItemId));
    return (await api.fetchCustom(`/branches/${branchId}?${query.toString()}`))
      .data as CustomResponse<SpringPage<RestaurantRecipeDTO>>;
  },
  listFiltered: async (
    branchId: number,
    request: RestaurantPageRequest<RestaurantRecipeFilters>
  ) => {
    const query = restaurantPageQuery(request.page, request.size, request.sort);
    const filters = request.filters;
    return (
      await api.addCustom(`/branches/${branchId}/filtered?${query}`, {
        search: filters.search?.trim() || null,
        categoryId: filters.categoryId ?? null,
        active: filters.active,
        menuItemId: filters.menuItemId,
      })
    ).data as RestaurantRecipePage;
  },
  get: async (branchId: number, id: number) =>
    (await api.fetchCustom(`/branches/${branchId}/${id}`))
      .data as CustomResponse<RestaurantRecipeDetailDTO>,
  create: async (branchId: number, form: RestaurantRecipeForm) =>
    (
      await api.addCustom(
        `/branches/${branchId}`,
        restaurantRecipePayload(form)
      )
    ).data as CustomResponse<RestaurantRecipeDetailDTO>,
  update: async (branchId: number, id: number, form: RestaurantRecipeForm) =>
    (
      await api.modifyCustom(
        `/branches/${branchId}/${id}`,
        restaurantRecipePayload(form)
      )
    ).data as CustomResponse<RestaurantRecipeDetailDTO>,
  deactivate: async (branchId: number, id: number) =>
    (await api.removeCustom(`/branches/${branchId}/${id}`))
      .data as CustomResponse<RestaurantRecipeDetailDTO>,
};

export default RestaurantRecipeService;
